import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { generateReport } from '../utils/reportGenerator';
import ReportModal from './ReportModal';
import ProjectModal from './ProjectModal';
import { useAuth } from './AuthProvider';
import './Navbar.css';

function Navbar({ selectedDisclosureId, isScrolled }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showProjectMenu, setShowProjectMenu] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [projects, setProjects] = useState([]); // Will store all projects
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const isESRSPage = location.pathname === '/esrs-reporting';

  // If we're on the home page, use the transparent header
  const isHomePage = location.pathname === '/';

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleGetStarted = () => {
    navigate('/esrs-reporting');
  };

  if (isHomePage) {
    return (
      <nav className={`top-nav ${isScrolled ? 'scrolled' : ''}`}>
        <div className="nav-content">
          <div className="nav-logo">
            <Link to="/">
              <img 
                src={process.env.PUBLIC_URL + '/images/Hympact_white.png'} 
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/Hympact_white.png';
                  console.log('Trying fallback path');
                }}
                alt="Hympact" 
                className="nav-logo-img" 
              />
            </Link>
          </div>
          <button className="mobile-menu-button" onClick={toggleMobileMenu}>
            {isMobileMenuOpen ? (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
              </svg>
            )}
          </button>
          <div className={`nav-links ${isMobileMenuOpen ? 'active' : ''}`}>
            <a href="#pricing" onClick={() => setIsMobileMenuOpen(false)}>PRICING</a>
            <a href="#features" onClick={() => setIsMobileMenuOpen(false)}>FEATURES</a>
            <a href="#enterprise" onClick={() => setIsMobileMenuOpen(false)}>ENTERPRISE</a>
            <a href="#blog" onClick={() => setIsMobileMenuOpen(false)}>BLOG</a>
            <a href="#forum" onClick={() => setIsMobileMenuOpen(false)}>FORUM</a>
            <a href="#careers" onClick={() => setIsMobileMenuOpen(false)}>CAREERS</a>
          </div>
          <div className="nav-right">
            <a 
              href="mailto:thibault.brun@hympact.ch" 
              className="contact-btn"
            >
              CONTACT
            </a>
          </div>
        </div>
      </nav>
    );
  }

  const handleProjectCreate = async (projectData) => {
    try {
      // TODO: Save to Supabase
      const newProject = {
        name: projectData.projectName,
        company: projectData.companyName,
        collaboratorEmail: projectData.collaboratorEmail
      };
      setCurrentProject(newProject);
      setProjects([...projects, newProject]);
      setShowProjectModal(false);
      setShowProjectMenu(false);
    } catch (error) {
      console.error('Error creating project:', error);
      alert('Error creating project. Please try again.');
    }
  };

  const handleProjectSelect = (project) => {
    setCurrentProject(project);
    setShowProjectMenu(false);
  };

  const handleGenerateReport = async (format) => {
    try {
      if (!selectedDisclosureId) {
        alert('Please select a disclosure requirement first');
        setShowReportModal(false);
        return;
      }

      setIsGenerating(true);

      // Fetch questions and answers for the current disclosure requirement
      const [questionsResponse, answersResponse] = await Promise.all([
        supabase
          .from('questions')
          .select('*')
          .eq('dr_id', selectedDisclosureId)
          .order('order_index'),
        supabase
          .from('answers')
          .select('*')
          .eq('dr_id', selectedDisclosureId)
      ]);

      if (questionsResponse.error) throw questionsResponse.error;
      if (answersResponse.error) throw answersResponse.error;

      // Transform answers into a map
      const answersMap = answersResponse.data.reduce((acc, answer) => {
        acc[answer.question_id] = answer.content;
        return acc;
      }, {});

      // Get the disclosure requirement title
      const { data: drData, error: drError } = await supabase
        .from('disclosure_requirements')
        .select('title')
        .eq('id', selectedDisclosureId)
        .single();

      if (drError) throw drError;

      // Generate the report with the correct data structure
      await generateReport(format, {
        title: drData.title,
        questions: questionsResponse.data,
        answers: answersMap
      });

      setShowReportModal(false);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Error generating report. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleLogout = () => {
    // TODO: Implement logout
    console.log('Logging out...');
  };

  if (isESRSPage) {
    return (
      <>
        <nav className="navbar esrs-navbar">
          <div className="navbar-left">
            <Link to="/" className="navbar-brand">
              <img 
                src="/images/hympact-logo.png" 
                alt="Hympact Logo" 
                className="navbar-logo"
              />
            </Link>
          </div>
          <div className="navbar-right">
            <button className="mobile-menu-button" onClick={toggleMobileMenu}>
              {isMobileMenuOpen ? (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
                </svg>
              )}
            </button>
            <div className={`mobile-actions ${isMobileMenuOpen ? 'active' : ''}`}>
              {currentProject ? (
                <div className="project-menu-container">
                  <button 
                    className="current-project"
                    onClick={() => setShowProjectMenu(!showProjectMenu)}
                  >
                    <span className="project-icon">📁</span>
                    {currentProject.name}
                    <span className="dropdown-icon">▼</span>
                  </button>
                  
                  {showProjectMenu && (
                    <div className="project-menu">
                      <button 
                        className="menu-item"
                        onClick={() => {
                          setShowProjectModal(true);
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        <span className="menu-icon">➕</span>
                        Create New Project
                      </button>
                      
                      {projects.length > 0 && (
                        <>
                          <div className="menu-divider"></div>
                          <div className="menu-section-title">Switch Project</div>
                          {projects.map((project, index) => (
                            <button
                              key={index}
                              className={`menu-item ${project === currentProject ? 'active' : ''}`}
                              onClick={() => {
                                handleProjectSelect(project);
                                setIsMobileMenuOpen(false);
                              }}
                            >
                              <span className="menu-icon">📁</span>
                              {project.name}
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <button 
                  className="nav-button create-project"
                  onClick={() => {
                    setShowProjectModal(true);
                    setIsMobileMenuOpen(false);
                  }}
                >
                  <span className="button-icon">➕</span>
                  Create Project
                </button>
              )}
              <button 
                className="nav-button generate-report"
                onClick={() => {
                  setShowReportModal(true);
                  setIsMobileMenuOpen(false);
                }}
                disabled={isGenerating}
              >
                <span className="button-icon">📄</span>
                {isGenerating ? 'Generating...' : 'Generate Report'}
              </button>
              
              <div className="user-menu-container">
                <button 
                  className="nav-button user-button"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                >
                  <span className="button-icon">👤</span>
                </button>
                
                {showUserMenu && (
                  <div className="user-menu">
                    <Link to="/profile" className="menu-item" onClick={() => setIsMobileMenuOpen(false)}>
                      <span className="menu-icon">👤</span>
                      Profile
                    </Link>
                    <Link to="/preferences" className="menu-item" onClick={() => setIsMobileMenuOpen(false)}>
                      <span className="menu-icon">⚙️</span>
                      Preferences
                    </Link>
                    <button onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }} className="menu-item logout">
                      <span className="menu-icon">🚪</span>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </nav>
        {showReportModal && (
          <ReportModal
            isOpen={showReportModal}
            onClose={() => setShowReportModal(false)}
            onGenerateReport={handleGenerateReport}
            isGenerating={isGenerating}
          />
        )}
        {showProjectModal && (
          <ProjectModal
            isOpen={showProjectModal}
            onClose={() => setShowProjectModal(false)}
            onSubmit={handleProjectCreate}
          />
        )}
      </>
    );
  }

  // Default navbar for other pages
  return (
    <nav className="navbar">
      <div className="navbar-left">
        <Link to="/" className="navbar-brand">
          <img 
            src="/images/hympact-logo.png" 
            alt="Hympact Logo" 
            className="navbar-logo"
          />
        </Link>
      </div>
      <div className="navbar-right">
        <Link to="/materiality-analysis" className="nav-button">Materiality Analysis</Link>
        {user ? (
          <>
            <Link to="/esrs-reporting" className="nav-button">ESRS Reporting</Link>
            <Link to="/dma" className="nav-button">Double Materiality Analysis</Link>
            <button onClick={handleLogout} className="nav-button">Logout</button>
          </>
        ) : (
          <>
            <Link to="/login" className="nav-button login">Login</Link>
            <Link to="/register" className="nav-button register">Register</Link>
          </>
        )}
      </div>
    </nav>
  );
}

export default Navbar; 